import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Settings,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader,
  CheckSquare,
  Square,
  Zap,
  Loader2
} from 'lucide-react';
import { contactFormOptionsAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import AdminSectionWrapper from './AdminSectionWrapper';

const ContactFormOptionsManagement = () => {
  const { notify } = useNotificationHook();
  const [options, setOptions] = useState({
    services: [],
    budgets: [],
    timelines: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('services');
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Bulk actions state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      setLoading(true);
      const response = await contactFormOptionsAPI.getAdminOptions();
      if (response?.data?.success) {
        setOptions(response.data.data);
      }
          } catch (error) {
        console.error('Error fetching contact form options:', error);
        notify.error('Failed to load options', 'Loading Error');
      } finally {
      setLoading(false);
    }
  };

  const handleSave = async (type, data) => {
    try {
      setSaving(true);
      let response;
      
      // Map type to correct API method name (singular)
      const methodMap = {
        services: 'Service',
        budgets: 'Budget', 
        timelines: 'Timeline'
      };
      
      const methodName = methodMap[type];
      
      if (editingItem) {
        response = await contactFormOptionsAPI[`update${methodName}`](editingItem.id, data);
      } else {
        response = await contactFormOptionsAPI[`create${methodName}`](data);
      }

      if (response?.data?.success) {
        await fetchOptions();
                  setEditingItem(null);
          setShowAddForm(false);
          notify.success(`${type.charAt(0).toUpperCase() + type.slice(1)} option ${editingItem ? 'updated' : 'created'} successfully!`, 'Success');
          setTimeout(() => setMessage(null), 3000);
        }
      } catch (error) {
        console.error(`Error ${editingItem ? 'updating' : 'creating'} ${type} option:`, error);
        notify.error(`Failed to ${editingItem ? 'update' : 'create'} ${type} option`, 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (window.confirm(`Are you sure you want to delete this ${type} option?`)) {
      try {
        setSaving(true);
        
        // Map type to correct API method name (singular)
        const methodMap = {
          services: 'Service',
          budgets: 'Budget', 
          timelines: 'Timeline'
        };
        
        const methodName = methodMap[type];
        const response = await contactFormOptionsAPI[`delete${methodName}`](id);
                  if (response?.data?.success) {
            await fetchOptions();
            notify.success(`${type.charAt(0).toUpperCase() + type.slice(1)} option deleted successfully!`, 'Deleted');
            setTimeout(() => setMessage(null), 3000);
          }
        } catch (error) {
          console.error(`Error deleting ${type} option:`, error);
          notify.error(`Failed to delete ${type} option`, 'Delete Error');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setEditingItem(null);
    setShowAddForm(false);
  };

  // Bulk actions functions
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSelected = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];
      
      setShowBulkActions(newSelected.length > 0);
      setSelectAll(newSelected.length === options[activeTab]?.length);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setShowBulkActions(false);
      setSelectAll(false);
    } else {
      const allItemIds = options[activeTab]?.map(item => item.id) || [];
      setSelectedItems(allItemIds);
      setShowBulkActions(true);
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (is_active) => {
    if (selectedItems.length === 0) return;

    try {
      setBulkActionLoading(true);
      const response = await contactFormOptionsAPI.bulkUpdateStatus(activeTab, selectedItems, is_active);
      
              if (response?.data?.success) {
          notify.success(`${selectedItems.length} ${activeTab.slice(0, -1)}${selectedItems.length !== 1 ? 's' : ''} ${is_active ? 'activated' : 'deactivated'} successfully`, 'Status Updated');
          setSelectedItems([]);
          setShowBulkActions(false);
          setSelectAll(false);
          fetchOptions();
        } else {
          notify.error(response?.data?.message || 'Failed to update items', 'Update Error');
        }
      } catch (error) {
        console.error('Error updating items:', error);
        notify.error('Failed to update items', 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} ${activeTab.slice(0, -1)}${selectedItems.length !== 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const response = await contactFormOptionsAPI.bulkDelete(activeTab, selectedItems);
      
              if (response?.data?.success) {
          notify.success(`${selectedItems.length} ${activeTab.slice(0, -1)}${selectedItems.length !== 1 ? 's' : ''} deleted successfully`, 'Items Deleted');
          setSelectedItems([]);
          setShowBulkActions(false);
          setSelectAll(false);
          fetchOptions();
        } else {
          notify.error(response?.data?.message || 'Failed to delete items', 'Delete Error');
        }
      } catch (error) {
        console.error('Error deleting items:', error);
        notify.error('Failed to delete items', 'Network Error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getTabIcon = (tab) => {
    const icons = {
      services: Settings,
      budgets: DollarSign,
      timelines: Clock
    };
    return icons[tab] || Settings;
  };

  const getTabLabel = (tab) => {
    const labels = {
      services: 'Services',
      budgets: 'Budget Ranges',
      timelines: 'Timelines'
    };
    return labels[tab] || tab;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AdminSectionWrapper>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Contact Form Options Management
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage dropdown options for Services, Budget, and Timeline in the Contact Us form
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
            : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {['services', 'budgets', 'timelines'].map((tab) => {
            const IconComponent = getTabIcon(tab);
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                {getTabLabel(tab)}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate(true)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  Activate
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate(false)}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <EyeOff className="h-3 w-3 mr-1" />}
                  Deactivate
                </button>
                
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 sm:p-6">
          {/* Header with Add Button */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {getTabLabel(activeTab)} Options
            </h3>
            <div className="flex items-center gap-4">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                {selectAll ? (
                  <CheckSquare className="h-4 w-4 text-blue-600" />
                ) : (
                  <Square className="h-4 w-4 text-gray-400" />
                )}
                Select All
              </button>
              <button
                onClick={() => setShowAddForm(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add {getTabLabel(activeTab).slice(0, -1)}
              </button>
            </div>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                {editingItem ? 'Edit' : 'Add'} {getTabLabel(activeTab).slice(0, -1)}
              </h4>
              <AddEditForm
                type={activeTab}
                item={editingItem}
                onSave={handleSave}
                onCancel={handleCancel}
                saving={saving}
              />
            </div>
          )}

          {/* Options List */}
          <div className="space-y-4">
            {options[activeTab]?.map((item) => (
              <div
                key={item.id}
                className={`flex items-center justify-between p-4 border rounded-lg ${
                  item.is_active 
                    ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800' 
                    : 'border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-700'
                }`}
              >
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleSelectItem(item.id)}
                    className="flex items-center"
                  >
                    {selectedItems.includes(item.id) ? (
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Square className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    {item.is_active ? (
                      <Eye className="w-4 h-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                    <span className={`font-medium ${
                      item.is_active 
                        ? 'text-gray-900 dark:text-white' 
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {item.label}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Value: {item.value}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Order: {item.sort_order}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(activeTab, item.id)}
                    className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminSectionWrapper>
  );
};

// Add/Edit Form Component
const AddEditForm = ({ type, item, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    value: item?.value || '',
    label: item?.label || '',
    is_active: item?.is_active !== false,
    sort_order: item?.sort_order || 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(type, formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Value *
          </label>
          <input
            type="text"
            name="value"
            value={formData.value}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., web_development"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Label *
          </label>
          <input
            type="text"
            name="label"
            value={formData.label}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., Web Development"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Sort Order
          </label>
          <input
            type="number"
            name="sort_order"
            value={formData.sort_order}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="0"
          />
        </div>
        <div className="flex items-center">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Active
            </span>
          </label>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {item ? 'Update' : 'Create'}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ContactFormOptionsManagement; 